import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import Card from "./Card";
import AuthImg from "./AuthImg";
import AuthFileLink from "./AuthFileLink";
import { toast } from "sonner";
import * as Icons from "lucide-react";

// La charte d'une entreprise se lit à DEUX endroits côté agence (décision du 25/10) : l'onglet
// « Brand Guide » de sa fiche, qui reste atteignable même sans le module Identité Visuelle — c'est
// un outil de production, Mélilia en a besoin pour fabriquer les publications —, et la page de son
// module Identité Visuelle. Un seul composant pour les deux : deux copies divergeraient au premier
// correctif. Côté entreprise, rien ne change : sa charte n'apparaît dans son menu que si le module
// est actif (décision du 15/10, question 13, dans components/Layout.js).
//
// Une seule donnée, un seul endroit où elle est écrite : `brand_guide` sur la fiche entreprise.

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : "—";
};

export default function BrandGuide({ clientId, client, onSaved }) {
  const logoRef = useRef(null);
  const [brand, setBrand] = useState({ colors: [], fonts: [], slogan: "" });

  useEffect(() => {
    const bg = client?.brand_guide || {};
    setBrand({ colors: bg.colors || [], fonts: bg.fonts || [], slogan: bg.slogan || "" });
  }, [client]);

  const logos = client?.brand_guide?.logos || [];
  const reload = () => { onSaved && onSaved(); };

  const save = async () => {
    try {
      await api.put(`/admin/clients/${clientId}/brand-guide`, { colors: brand.colors, fonts: brand.fonts, slogan: brand.slogan });
      toast.success("Brand Guide enregistré");
      reload();
    } catch { toast.error("Échec de l'enregistrement"); }
  };
  const uploadLogo = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { await api.post(`/admin/clients/${clientId}/brand-logo`, fd); toast.success("Logo ajouté"); reload(); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Échec du téléversement"); }
    if (logoRef.current) logoRef.current.value = "";
  };
  const deleteLogo = async (lid) => { await api.delete(`/admin/clients/${clientId}/brand-logo/${lid}`); toast.success("Logo supprimé"); reload(); };

  const addColor = () => setBrand({ ...brand, colors: [...brand.colors, { name: "", hex: "#33338A" }] });
  const updateColor = (i, k, v) => setBrand({ ...brand, colors: brand.colors.map((c, idx) => idx === i ? { ...c, [k]: v } : c) });
  const removeColor = (i) => setBrand({ ...brand, colors: brand.colors.filter((_, idx) => idx !== i) });
  const addFont = () => setBrand({ ...brand, fonts: [...brand.fonts, { name: "", usage: "" }] });
  const updateFont = (i, k, v) => setBrand({ ...brand, fonts: brand.fonts.map((f, idx) => idx === i ? { ...f, [k]: v } : f) });
  const removeFont = (i) => setBrand({ ...brand, fonts: brand.fonts.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6" data-testid="client-brand-guide">
      <Card title="Logos" action={logos.length < 3 ? <><button onClick={() => logoRef.current.click()} data-testid="brand-logo-upload" className="text-sm border border-marine text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1"><Icons.Upload size={16} /> Importer un logo</button><input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} /></> : <span className="text-xs text-marine/80">3 / 3 max</span>}>
        <p className="text-sm text-marine/80 mb-4">De 1 à 3 logos (principal, secondaire, monochrome / favicon).</p>
        {logos.length === 0 ? <p className="text-sm text-marine/80">Aucun logo importé.</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {logos.map((l) => (
              <div key={l.id} data-testid={`brand-logo-${l.id}`} className="rounded-sm border border-marine/10 p-3">
                <div className="aspect-square rounded-sm bg-[repeating-conic-gradient(#EDEDED_0_25%,#fff_0_50%)] bg-[length:16px_16px] flex items-center justify-center overflow-hidden mb-2">
                  <AuthImg path={l.path} alt={l.label} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-marine truncate">{l.label}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <AuthFileLink path={l.path} title="Télécharger" className="p-1 text-marine hover:bg-marine-50 rounded-sm inline-flex"><Icons.Download size={16} /></AuthFileLink>
                    <button onClick={() => deleteLogo(l.id)} data-testid={`brand-logo-delete-${l.id}`} title="Supprimer" className="p-1 text-rouge hover:bg-rouge-50 rounded-sm"><Icons.Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Palette de couleurs" action={<button onClick={addColor} data-testid="brand-add-color" className="text-sm border border-marine text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1"><Icons.Plus size={16} /> Ajouter une couleur</button>}>
        {brand.colors.length === 0 ? <p className="text-sm text-marine/80">Aucune couleur. Créez la palette de la marque.</p> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {brand.colors.map((col, i) => (
              <div key={i} data-testid={`brand-color-${i}`} className="flex items-center gap-3 rounded-sm border border-marine/10 p-3">
                <input type="color" value={col.hex || "#000000"} onChange={(e) => updateColor(i, "hex", e.target.value)} data-testid={`color-swatch-${i}`} className="w-12 h-12 rounded-sm border border-marine/10 cursor-pointer shrink-0 p-0" />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <input value={col.name || ""} onChange={(e) => updateColor(i, "name", e.target.value)} data-testid={`color-name-${i}`} className="inp text-sm" placeholder="Nom (optionnel) — ex. Bleu marine" />
                  <div className="flex items-center gap-2">
                    <input value={col.hex || ""} onChange={(e) => updateColor(i, "hex", e.target.value)} data-testid={`color-hex-${i}`} className="inp text-sm font-mono w-28" placeholder="#000000" />
                    <span className="text-[11px] text-marine/80">RGB {hexToRgb(col.hex)}</span>
                  </div>
                </div>
                <button onClick={() => removeColor(i)} data-testid={`color-remove-${i}`} className="text-rouge hover:opacity-70 shrink-0"><Icons.Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Typographie" action={<button onClick={addFont} data-testid="brand-add-font" className="text-sm border border-marine text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1"><Icons.Plus size={16} /> Ajouter une police</button>}>
        {brand.fonts.length === 0 ? <p className="text-sm text-marine/80">Aucune police renseignée.</p> : (
          <div className="space-y-3">
            {brand.fonts.map((ft, i) => (
              <div key={i} data-testid={`brand-font-${i}`} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end rounded-sm border border-marine/10 p-3">
                <label className="text-sm">Nom de la police<input value={ft.name || ""} onChange={(e) => updateFont(i, "name", e.target.value)} data-testid={`font-name-${i}`} className="inp mt-1" placeholder="Ex. Montserrat" /></label>
                <label className="text-sm">Utilisation<input value={ft.usage || ""} onChange={(e) => updateFont(i, "usage", e.target.value)} data-testid={`font-usage-${i}`} className="inp mt-1" placeholder="Titre, Texte courant, Accent..." /></label>
                <button onClick={() => removeFont(i)} data-testid={`font-remove-${i}`} className="text-rouge hover:opacity-70 justify-self-end pb-2"><Icons.Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Slogan / Signature de marque">
        <textarea value={brand.slogan} onChange={(e) => setBrand({ ...brand, slogan: e.target.value })} data-testid="brand-slogan" rows={2} className="inp w-full resize-y" placeholder="Ex. Votre sourire, notre expertise." />
      </Card>

      <div className="flex justify-end"><button onClick={save} data-testid="brand-save" className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover transition-colors">Enregistrer le Brand Guide</button></div>
    </div>
  );
}
